import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Ticket, FileText, CreditCard, CircleUser, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';

const benefits: { icon: React.ReactNode; title: string; description: string }[] = [
  {
    icon: <Ticket className="h-5 w-5 text-[#5865F2]" />,
    title: 'Suivi des demandes',
    description: 'Consultez vos tickets et l’historique de vos interventions en un coup d’œil.',
  },
  {
    icon: <FileText className="h-5 w-5 text-[#5865F2]" />,
    title: 'Factures centralisées',
    description: 'Retrouvez vos factures et documents sans fouiller dans vos e-mails.',
  },
  {
    icon: <CreditCard className="h-5 w-5 text-[#5865F2]" />,
    title: 'Abonnement et paiements',
    description: 'Gérez votre formule, vos moyens de paiement et votre facturation.',
  },
  {
    icon: <CircleUser className="h-5 w-5 text-[#5865F2]" />,
    title: 'Profil et préférences',
    description: 'Mettez à jour vos coordonnées et vos préférences de contact.',
  },
];

const ClientSpaceSection: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { openRegisterModal, openLoginModal } = useModal();

  return (
    <section id="espace-client" className="py-16 md:py-20 bg-[#2F3136] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-12 md:mb-14"
          initial={{ opacity: 1, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.08, margin: '0px 0px -8% 0px' }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          <p className="inline-flex items-center gap-2 text-[#C7D0FF] text-xs uppercase tracking-wider mb-3">
            <Sparkles className="h-3.5 w-3.5" />
            Gratuit et sécurisé
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Votre espace client : tout centraliser au même endroit
          </h2>
          <p className="text-gray-300 text-base md:text-lg">
            Créez un compte en quelques secondes pour suivre vos demandes, vos factures et votre abonnement.
            Aucune carte bancaire requise pour vous inscrire.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {benefits.map((item, index) => (
            <motion.div
              key={item.title}
              className="rounded-xl border border-white/10 bg-[#36393F]/80 p-5 shadow-lg shadow-black/20"
              initial={{ opacity: 1, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.08, margin: '0px 0px -8% 0px' }}
              transition={{ duration: 0.4, delay: index * 0.06, ease: 'easeOut' }}
            >
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#5865F2]/15">
                {item.icon}
              </div>
              <h3 className="text-white font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
          initial={{ opacity: 1, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.08, margin: '0px 0px -8% 0px' }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {isAuthenticated ? (
            <Link
              to="/mon-espace"
              className="inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold text-white bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] rounded-lg shadow-lg hover:shadow-[#5865F2]/35 transition-all duration-300 hover:scale-[1.02]"
            >
              Accéder à mon espace
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          ) : (
            <>
              <button
                type="button"
                onClick={() => openRegisterModal()}
                className="inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold text-white bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] rounded-lg shadow-lg hover:shadow-[#5865F2]/35 transition-all duration-300 hover:scale-[1.02]"
              >
                Créer mon espace gratuit
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => openLoginModal()}
                className="text-sm font-medium text-gray-400 hover:text-white underline-offset-4 hover:underline transition-colors"
              >
                Déjà un compte ? Connexion
              </button>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ClientSpaceSection;
